import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Ticket, Package, HelpCircle, Menu, X, Search, Crown, Building2 } from "lucide-react";
import { useState, useLayoutEffect } from "react";
import { Button } from "./ui/button";

export const Layout = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Reset scroll on every navigation so users don’t land on the footer with preserved scroll position
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  const navLinks = [
    { path: "/", label: "Classes", icon: Calendar },
    { path: "/events", label: "Events", icon: Ticket },
    { path: "/packages", label: "Packages", icon: Package },
    { path: "/private-sessions", label: "Private", icon: Crown },
    { path: "/studio-booking", label: "Studio", icon: Building2 },
    { path: "/support", label: "Support", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="container-empire py-4">
          <nav className="glass rounded-2xl px-6 py-3 flex items-center justify-between">
            {/* Logo - white version for dark header background */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img
                src="/branding/logo-light.png"
                alt="Empire Dance Kenya"
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling?.classList.remove("hidden");
                }}
              />
              <span className="hidden font-cookie text-3xl text-empire-gold">
                Empire <span className="text-white font-spartan font-semibold hidden sm:inline">Dance Kenya</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path ||
                  (link.path === "/" && location.pathname === "/schedule");

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 group ${isActive
                        ? "bg-empire-gold/10 text-empire-gold"
                        : "text-neutral-400 hover:text-empire-gold hover:bg-white/5"
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium relative after:absolute after:left-0 after:bottom-0 after:h-px after:w-0 after:bg-empire-gold after:transition-all after:duration-200 group-hover:after:w-full">{link.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-3">
              <Link to="/lookup" data-testid="lookup-booking-btn">
                <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-white">
                  <Search className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Find Booking</span>
                </Button>
              </Link>

              {/* Mobile menu button */}
              <button
                className="md:hidden text-white p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden glass mx-4 mt-2 rounded-2xl p-4"
            >
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive
                        ? "bg-empire-gold/10 text-empire-gold"
                        : "text-neutral-400 hover:text-empire-gold"
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t border-neutral-800 mt-8">
        <div className="container-empire py-12">
          {/* Main footer content */}
          <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-8">

            {/* Brand section */}
            <div className="lg:max-w-sm">
              <img
                src="/branding/logo-light.png"
                alt="Empire Dance Kenya"
                className="h-14 w-auto object-contain mb-4"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling?.classList.remove("hidden");
                }}
              />
              <span className="hidden font-cookie text-2xl text-empire-gold">Empire Dance Kenya</span>
              <p className="text-neutral-400 leading-relaxed mb-5">
                East Africa's premier urban dance studio, nurturing talent and building community through the power of movement since 2017.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.tiktok.com/@empiredancekenya"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-empire-gold transition-colors font-medium"
                >
                  TikTok
                </a>
                <span className="text-neutral-700">·</span>
                <a
                  href="https://www.instagram.com/empiredancekenya"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-empire-gold transition-colors font-medium"
                >
                  Instagram
                </a>
              </div>
            </div>

            {/* Links and info */}
            <div className="flex flex-col sm:flex-row gap-12 sm:gap-16 lg:gap-20">
              {/* Quick Links */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-empire-gold mb-4">Quick Links</h4>
                <ul className="space-y-2.5">
                  {[
                    { to: "/events", label: "Events" },
                    { to: "/packages", label: "Packages" },
                    { to: "/private-sessions", label: "Private Sessions" },
                  ].map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="text-neutral-400 hover:text-empire-gold transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Location */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-empire-gold mb-4">Visit Us</h4>
                <p className="text-neutral-300 font-medium">Kenrail Towers, 3rd Floor</p>
                <p className="text-neutral-400">Southern Wing, Westlands, Nairobi</p>
              </div>

              {/* Hours */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-empire-gold mb-4">Hours</h4>
                <div className="space-y-1.5">
                  <div className="flex gap-4">
                    <span className="text-neutral-300 w-20">Mon – Sun</span>
                    <span className="text-neutral-400">9 AM – 8:30 PM</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-neutral-900">
          <div className="container-empire py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-neutral-500">© {new Date().getFullYear()} Empire Dance Kenya. All rights reserved.</p>
            <Link to="/support" className="text-sm text-neutral-500 hover:text-empire-gold transition-colors">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
